import { useEffect, useRef } from 'react';
import type { RestoreOutcome, SlotDemoScene } from '@goodtime/engine';
import {
  isSlotUnlocked,
  ROAD_NEXT_SLOT,
  WORLD_NODES,
  type RoadRestoreResponse,
  type RoadState,
} from '@goodtime/shared';
import { funnelBeacon } from '../../api/funnel';
import { restoreShort } from '../../api/restore';
import { safeAreaBottom } from './safeArea';

const NO_SLOTS: readonly string[] = [];
const noRoad = () => false;

/**
 * Hands the road's landmarks to the slot scene, which draws them under the
 * SPIN row with their restore cards and the chapter's end (Phase 2a), and
 * restores one through the server when the player asks. When the last
 * restore opens the next slot, `onSlotOpened` hears of it, and the
 * chapter's card plays that slot through `onPlaySlot`. A next slot the
 * player already has (`unlockedSlotIds`) is never promised as a prize.
 */
export function useLandmarkRoad({
  scene,
  road,
  themeId,
  playerId,
  unlockedSlotIds = NO_SLOTS,
  roadOn = noRoad,
  onRestore,
  onSlotOpened,
  onPlaySlot,
}: {
  scene: SlotDemoScene | null;
  road: RoadState | null;
  themeId: string | null;
  playerId?: string | null;
  /** The slots the player has opened (world-map node ids). */
  unlockedSlotIds?: readonly string[];
  /** Whether a slot has a road for this player, for the chapter card. */
  roadOn?: (themeId: string) => boolean;
  onRestore?: (landmark: number) => Promise<RoadRestoreResponse>;
  onSlotOpened?: (slotId: string) => void;
  onPlaySlot?: (slotId: string) => void;
}): void {
  const handlers = useRef({ roadOn, onRestore, onSlotOpened, onPlaySlot });
  handlers.current = { roadOn, onRestore, onSlotOpened, onPlaySlot };
  const landmarks = road?.landmarks ?? null;
  const roadStep = road?.roadStep;
  const chapter = road?.chapter ?? 1;
  const hasRoad = landmarks !== null;

  useEffect(() => {
    if (!scene) return;
    if (!landmarks || !themeId || !handlers.current.onRestore) {
      scene.setLandmarkRoad(null);
      return;
    }
    const next = ROAD_NEXT_SLOT[themeId];
    const nextNode = next && WORLD_NODES.find((n) => n.id === next.node);
    scene.setLandmarkRoad({
      themeId,
      chapter,
      landmarks,
      safeBottom: safeAreaBottom(),
      next: nextNode
        ? {
            name: nextNode.name,
            road: handlers.current.roadOn(next.theme),
            owned: isSlotUnlocked(nextNode.id, unlockedSlotIds),
            play: () => handlers.current.onPlaySlot?.(String(nextNode.id)),
          }
        : null,
      onOpened: (i) => funnelBeacon('restore_tapped', { playerId, detail: `${landmarks[i]?.id ?? i}` }),
      restore: async (i): Promise<RestoreOutcome> => {
        try {
          const res = await handlers.current.onRestore!(i);
          if (res.unlockedSlotId) handlers.current.onSlotOpened?.(res.unlockedSlotId);
          return {
            kind: 'restored',
            charged: res.charged,
            chapterComplete: res.chapterComplete,
            opened: res.unlockedSlotId != null,
          };
        } catch (err) {
          const short = restoreShort(err);
          return short ? { kind: 'short', coins: short.coins } : { kind: 'failed' };
        }
      },
    });
    // The landmarks only change with the road's step; a new array from each
    // spin's road state needn't rebuild the road.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scene, roadStep, hasRoad, themeId, chapter, playerId, unlockedSlotIds]);

  useEffect(() => () => scene?.setLandmarkRoad(null), [scene]);
}
